import React, { useEffect, useRef } from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { BOOKING_STATUS_TONE } from '@/constants/booking-status';
import { usePlanFeature } from '@/hooks/use-plan-feature';
import { navigateToSubscriptionPlans } from '@/lib/auth/subscription-plans-url';
import { showPlanFeature } from '@/store/planFeatureStore';
import type { Service } from '@/types/service';
import { RECURRING_BOOKING_MODE } from '@/types/recurring-booking-mode';

/** Serviço que só funciona com plano: assinatura ou recorrência do cliente. */
export function isRecurringService(
  service: Pick<Service, 'isSubscription' | 'recurringBookingMode'>,
): boolean {
  return (
    !!service.isSubscription ||
    (service.recurringBookingMode ?? RECURRING_BOOKING_MODE.OFF) !== RECURRING_BOOKING_MODE.OFF
  );
}

/**
 * Assinatura criada no onboarding (ou antes do teste acabar) por quem está no
 * gratuito. Ao abrir o serviço, o aviso de plano aparece uma vez; o banner
 * fica, porque o estado é da conta, não do toque. Mesmo visual do
 * `BookingStatusBanner` logo acima.
 */
export function SubscriptionPausedBanner({ service }: { service: Service }) {
  const plan = usePlanFeature('SUBSCRIPTIONS');
  const paused = plan.locked && service.isActive !== false && isRecurringService(service);

  const shown = useRef(false);
  useEffect(() => {
    if (!paused || shown.current) return;
    shown.current = true;
    showPlanFeature('SUBSCRIPTIONS');
  }, [paused]);

  if (!paused) return null;
  const tone = BOOKING_STATUS_TONE.warning;

  return (
    <View
      className="gap-2 rounded-2xl p-4"
      style={{ backgroundColor: tone.background, borderCurve: 'continuous' }}
    >
      <View className="flex-row items-center gap-2">
        <Ionicons name="pause-circle-outline" size={17} color={tone.foreground} />
        <ThemedText className="flex-1 text-sm font-bold" style={{ color: tone.foreground }}>
          Pausado no plano gratuito
        </ThemedText>
      </View>
      <ThemedText className="text-xs leading-4" style={{ color: tone.foreground }}>
        {service.isSubscription
          ? 'Ninguém assina nem marca por esta assinatura até você assinar um plano. Se preferir, edite e troque para sessão avulsa.'
          : 'A marcação recorrente fica parada até você assinar um plano. Sessões avulsas seguem normais.'}
      </ThemedText>
      <PressableScale
        haptic
        accessibilityRole="button"
        onPress={() =>
          void navigateToSubscriptionPlans({ push: (path) => router.push(path as never) })
        }
        className="mt-1 self-start rounded-full bg-card px-4 py-2"
        style={{ borderCurve: 'continuous' }}
      >
        <ThemedText className="text-xs font-bold" style={{ color: tone.foreground }}>
          Ver planos
        </ThemedText>
      </PressableScale>
    </View>
  );
}
